// src/components/themes/default/admin/BranchDailyCollectionTab.tsx
"use client";

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  CalendarDays,
  CircleDollarSign,
  PackageCheck,
  ShoppingBag,
  FileSpreadsheet,
  FileText,
  Search,
  Filter,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertCircle,
  Calendar as CalendarIcon,
} from "lucide-react";

interface BranchDailyCollectionTabProps {
  branchInfo: any;
  subdomain: string;
  theme?: "dark" | "light";
}

interface CustomDatePickerProps {
  selectedDate: string;
  onChange: (dateStr: string) => void;
  onClose: () => void;
  align?: "left" | "right";
}

function CustomDatePicker({
  selectedDate,
  onChange,
  onClose,
  align = "left",
}: CustomDatePickerProps) {
  const popupRef = useRef<HTMLDivElement>(null);

  const initDate = selectedDate ? new Date(selectedDate) : new Date();
  const [viewYear, setViewYear] = useState<number>(
    isNaN(initDate.getTime()) ? new Date().getFullYear() : initDate.getFullYear()
  );
  const [viewMonth, setViewMonth] = useState<number>(
    isNaN(initDate.getTime()) ? new Date().getMonth() : initDate.getMonth()
  );

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((prev) => prev - 1);
    } else {
      setViewMonth((prev) => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((prev) => prev + 1);
    } else {
      setViewMonth((prev) => prev + 1);
    }
  };

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDayIndex = new Date(viewYear, viewMonth, 1).getDay();
  const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();

  const daysCells = useMemo(() => {
    const cells: Array<{
      dayNumber: number;
      isCurrentMonth: boolean;
      dateStr: string;
    }> = [];

    for (let i = firstDayIndex - 1; i >= 0; i--) {
      const d = daysInPrevMonth - i;
      const prevM = viewMonth === 0 ? 11 : viewMonth - 1;
      const prevY = viewMonth === 0 ? viewYear - 1 : viewYear;
      const mStr = String(prevM + 1).padStart(2, "0");
      const dStr = String(d).padStart(2, "0");
      cells.push({
        dayNumber: d,
        isCurrentMonth: false,
        dateStr: `${prevY}-${mStr}-${dStr}`,
      });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const mStr = String(viewMonth + 1).padStart(2, "0");
      const dStr = String(i).padStart(2, "0");
      cells.push({
        dayNumber: i,
        isCurrentMonth: true,
        dateStr: `${viewYear}-${mStr}-${dStr}`,
      });
    }

    const remaining = (7 - (cells.length % 7)) % 7;
    for (let i = 1; i <= remaining; i++) {
      const nextM = viewMonth === 11 ? 0 : viewMonth + 1;
      const nextY = viewMonth === 11 ? viewYear + 1 : viewYear;
      const mStr = String(nextM + 1).padStart(2, "0");
      const dStr = String(i).padStart(2, "0");
      cells.push({
        dayNumber: i,
        isCurrentMonth: false,
        dateStr: `${nextY}-${mStr}-${dStr}`,
      });
    }

    return cells;
  }, [viewYear, viewMonth, daysInMonth, firstDayIndex, daysInPrevMonth]);

  return (
    <div
      ref={popupRef}
      className={`absolute top-full mt-2.5 z-50 w-[275px] bg-white rounded-xl shadow-2xl border border-slate-200 p-4 font-sans text-slate-800 animate-in fade-in zoom-in-95 duration-150 ${
        align === "right" ? "right-0" : "left-0"
      }`}
    >
      <div
        className={`absolute -top-1.5 w-3 h-3 bg-white border-t border-l border-slate-200 transform rotate-45 ${
          align === "right" ? "right-6" : "left-6"
        }`}
      />

      <div className="flex items-center justify-between pb-3 px-1 border-b border-slate-100">
        <button
          type="button"
          onClick={handlePrevMonth}
          className="text-slate-400 hover:text-slate-700 text-lg font-bold px-1.5 py-0.5 rounded hover:bg-slate-100 transition-colors"
          title="Previous Month"
        >
          «
        </button>

        <span className="text-[13px] font-bold text-slate-800 tracking-tight">
          {monthNames[viewMonth]} {viewYear}
        </span>

        <button
          type="button"
          onClick={handleNextMonth}
          className="text-slate-400 hover:text-slate-700 text-lg font-bold px-1.5 py-0.5 rounded hover:bg-slate-100 transition-colors"
          title="Next Month"
        >
          »
        </button>
      </div>

      <div className="grid grid-cols-7 text-center text-[11px] font-semibold text-slate-700 pt-2.5 pb-1">
        <span>Su</span>
        <span>Mo</span>
        <span>Tu</span>
        <span>We</span>
        <span>Th</span>
        <span>Fr</span>
        <span>Sa</span>
      </div>

      <div className="grid grid-cols-7 gap-y-1 text-center text-xs">
        {daysCells.map((cell, idx) => {
          const isSelected = selectedDate === cell.dateStr;

          return (
            <button
              key={idx}
              type="button"
              onClick={() => {
                onChange(cell.dateStr);
                onClose();
              }}
              className={`w-8 h-8 mx-auto flex items-center justify-center text-xs transition-all cursor-pointer ${
                isSelected
                  ? "bg-[#18887b] text-white font-bold rounded-lg shadow-sm"
                  : cell.isCurrentMonth
                  ? "text-slate-700 hover:bg-slate-100 rounded-lg font-medium"
                  : "text-slate-300 font-normal hover:text-slate-400"
              }`}
            >
              {cell.dayNumber}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function BranchDailyCollectionTab({
  branchInfo,
  subdomain = "nextlook",
  theme: propTheme,
}: BranchDailyCollectionTabProps) {
  const [currentTheme, setCurrentTheme] = useState<"dark" | "light">(propTheme || "dark");

  useEffect(() => {
    if (propTheme) {
      setCurrentTheme(propTheme);
      return;
    }

    const checkActiveTheme = () => {
      if (typeof window === "undefined") return;
      const isLocalLight = localStorage.getItem("admin_theme") === "light";
      const isDocDark = document.documentElement.classList.contains("dark");
      setCurrentTheme(isLocalLight || !isDocDark ? "light" : "dark");
    };

    checkActiveTheme();
    const observer = new MutationObserver(checkActiveTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    window.addEventListener("storage", checkActiveTheme);

    return () => {
      observer.disconnect();
      window.removeEventListener("storage", checkActiveTheme);
    };
  }, [propTheme]);

  const isLight = currentTheme === "light";

  const getTodayDate = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [fromDate, setFromDate] = useState<string>(getTodayDate);
  const [toDate, setToDate] = useState<string>(getTodayDate);
  const [searchQuery, setSearchQuery] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("all");

  const [activePicker, setActivePicker] = useState<"from" | "to" | null>(null);

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<any[]>([]);
  const [summary, setSummary] = useState({
    total_delivered_orders: 0,
    total_collected: 0,
    total_items_sold: 0,
  });

  const getApiBase = useCallback(() => {
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      if (host === "localhost" || host === "127.0.0.1" || host.includes("localhost")) {
        return `http://${subdomain || "nextlook"}.localhost:8000`;
      }
      return "";
    }
    return "";
  }, [subdomain]);

  const fetchCollectionData = useCallback(async () => {
    if (!branchInfo?.id) return;
    setLoading(true);
    try {
      const apiBase = getApiBase();
      const storageKey = `branch_auth_${subdomain}_${branchInfo.slug}`;
      const authData = JSON.parse(localStorage.getItem(storageKey) || "{}");
      const token = authData.token || localStorage.getItem("token") || "";

      const params = new URLSearchParams({
        branch_id: String(branchInfo.id),
        from_date: fromDate || "all",
        to_date: toDate || "all",
        payment_method: paymentMethod,
        search: searchQuery,
      });

      const res = await fetch(`${apiBase}/api/v1/orders/daily-collection/?${params.toString()}`, {
        headers: {
          "Content-Type": "application/json",
          "X-Branch-ID": String(branchInfo.id),
          ...(token ? { Authorization: `Bearer ${token.trim()}` } : {}),
        },
      });

      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
        if (data.summary) {
          setSummary(data.summary);
        }
      }
    } catch (err) {
      console.error("Branch daily collection fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [getApiBase, branchInfo?.id, branchInfo?.slug, subdomain, fromDate, toDate, paymentMethod, searchQuery]);

  useEffect(() => {
    fetchCollectionData();
  }, [fetchCollectionData]);

  const handleSetQuickDate = (type: "today" | "yesterday" | "month" | "all") => {
    const today = new Date();
    if (type === "today") {
      const t = getTodayDate();
      setFromDate(t);
      setToDate(t);
    } else if (type === "yesterday") {
      const yest = new Date(today);
      yest.setDate(today.getDate() - 1);
      const year = yest.getFullYear();
      const month = String(yest.getMonth() + 1).padStart(2, "0");
      const day = String(yest.getDate()).padStart(2, "0");
      const yStr = `${year}-${month}-${day}`;
      setFromDate(yStr);
      setToDate(yStr);
    } else if (type === "month") {
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const firstDay = `${year}-${month}-01`;
      const lastDay = getTodayDate();
      setFromDate(firstDay);
      setToDate(lastDay);
    } else {
      setFromDate("");
      setToDate("");
    }
    setActivePicker(null);
  };

  const formatInputDisplay = (dateStr: string) => {
    if (!dateStr) return "Select Date";
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateStr;
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, fromDate, toDate, paymentMethod, itemsPerPage]);

  const totalPages = Math.ceil(orders.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, orders.length);
  const paginatedOrders = useMemo(() => {
    return orders.slice(startIndex, startIndex + itemsPerPage);
  }, [orders, startIndex, itemsPerPage]);

  const handleExportCSV = () => {
    if (orders.length === 0) return alert("Export করার মতো কোনো রেকর্ড নেই।");

    const headers = [
      "Date & Time",
      "Order ID",
      "Customer Name",
      "Phone",
      "Products",
      "Payment Method",
      "Collected Amount (BDT)",
    ];
    const rows = orders.map((o) => [
      `"${o.date}"`,
      `"${o.order_number}"`,
      `"${(o.customer_name || "").replace(/"/g, '""')}"`,
      `"${o.customer_phone || ""}"`,
      `"${(o.products || "").replace(/"/g, '""')}"`,
      `"${o.payment_method}"`,
      o.amount,
    ]);

    const csvContent = "\uFEFF" + [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `${branchInfo?.name || "branch"}-collection-${fromDate || "all"}-to-${toDate || "all"}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPDF = () => {
    if (orders.length === 0) return alert("PDF তৈরি করার মতো কোনো রেকর্ড নেই।");

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const rowsHtml = orders
      .map(
        (o) => `
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #e2e8f0; font-size: 11px;">${o.date}</td>
          <td style="padding: 8px; border-bottom: 1px solid #e2e8f0; font-weight: bold; font-family: monospace;">#${o.order_number}</td>
          <td style="padding: 8px; border-bottom: 1px solid #e2e8f0;">${o.customer_name}</td>
          <td style="padding: 8px; border-bottom: 1px solid #e2e8f0; font-family: monospace;">${o.customer_phone}</td>
          <td style="padding: 8px; border-bottom: 1px solid #e2e8f0; font-size: 11px;">${o.products}</td>
          <td style="padding: 8px; border-bottom: 1px solid #e2e8f0; text-transform: uppercase; font-size: 10px;">${o.payment_method}</td>
          <td style="padding: 8px; border-bottom: 1px solid #e2e8f0; text-align: right; color: #059669; font-weight: bold;">৳${o.amount.toLocaleString()}</td>
        </tr>
      `
      )
      .join("");

    printWindow.document.write(`
      <html>
        <head>
          <title>${branchInfo?.name || "Branch"} - Daily Collection Report</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 25px; color: #0f172a; }
            .header { display: flex; justify-content: space-between; border-bottom: 2px solid #0f172a; padding-bottom: 12px; margin-bottom: 18px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th { text-align: left; background: #f1f5f9; padding: 10px 8px; text-transform: uppercase; font-size: 10px; }
            .footer-row { background: #f8fafc; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <h2 style="margin: 0; text-transform: uppercase;">${branchInfo?.name || "Branch"} - Daily Collection</h2>
              <p style="margin: 4px 0 0 0; color: #64748b; font-size: 12px;">Delivered Orders Cash Audit</p>
            </div>
            <div style="text-align: right;">
              <h3 style="margin: 0; color: #059669;">Total Collection: ৳${summary.total_collected.toLocaleString()}</h3>
              <p style="margin: 4px 0 0 0; color: #64748b; font-size: 12px;">Delivered Orders: ${summary.total_delivered_orders}</p>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Date & Time</th><th>Order #</th><th>Customer</th><th>Phone</th>
                <th>Products / Items</th><th>Method</th><th style="text-align: right;">Collected Bill</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
              <tr class="footer-row">
                <td colspan="6" style="padding: 10px 8px; text-transform: uppercase;">Total Delivered Collection</td>
                <td style="padding: 10px 8px; text-align: right; color: #059669;">৳${summary.total_collected.toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="w-full px-3.5 sm:px-5 lg:px-6 py-4 sm:py-6 space-y-5 font-sans transition-colors duration-200">
      {/* ১. হেডার ও টাইটেল */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className={`text-lg sm:text-xl font-black flex items-center gap-2.5 ${isLight ? "text-slate-900" : "text-white"}`}>
            <CalendarDays className="w-5 h-5 text-emerald-500" />
            <span>Daily Order Collection</span>
          </h2>
          <p className={`text-xs ${isLight ? "text-slate-500" : "text-slate-400"}`}>
            {branchInfo?.name} delivered orders cash collection, item breakdown and verified revenue audit
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <button
            onClick={handleExportCSV}
            className={`px-3.5 py-2 rounded-xl font-bold transition-all text-xs flex items-center gap-2 cursor-pointer shadow-sm ${
              isLight
                ? "bg-white hover:bg-slate-50 text-emerald-600 border border-slate-200 shadow-slate-200/50"
                : "bg-slate-900 hover:bg-slate-800 text-emerald-400 border border-emerald-500/30"
            }`}
          >
            <FileSpreadsheet className="w-4 h-4" />
            <span>Generate CSV</span>
          </button>

          <button
            onClick={handleExportPDF}
            className={`px-3.5 py-2 rounded-xl font-bold transition-all text-xs flex items-center gap-2 cursor-pointer shadow-sm ${
              isLight
                ? "bg-white hover:bg-slate-50 text-rose-600 border border-slate-200 shadow-slate-200/50"
                : "bg-slate-900 hover:bg-slate-800 text-rose-400 border border-rose-500/30"
            }`}
          >
            <FileText className="w-4 h-4" />
            <span>Generate PDF</span>
          </button>

          <button
            onClick={fetchCollectionData}
            disabled={loading}
            className={`p-2.5 rounded-xl transition-all cursor-pointer ${
              isLight
                ? "bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 shadow-sm"
                : "bg-slate-900 hover:bg-slate-800 text-slate-300 border border-slate-800"
            }`}
            title="Refresh Data"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin text-emerald-500" : ""}`} />
          </button>
        </div>
      </div>

      {/* ২. ৩টি ভাইব্রেন্ট কার্ড */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Card 1: Total Cash Collected */}
        <div
          className={`p-5 rounded-2xl sm:rounded-3xl shadow-lg relative overflow-hidden transition-all duration-300 ${
            isLight
              ? "bg-gradient-to-br from-emerald-500 via-teal-600 to-teal-700 text-white shadow-teal-500/25 border-0 hover:scale-[1.01]"
              : "bg-gradient-to-br from-emerald-950/40 to-slate-900 border border-emerald-500/30 text-white"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className={`text-xs font-black uppercase tracking-wider ${isLight ? "text-emerald-100" : "text-emerald-400"}`}>
              Total Cash Collected
            </span>
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${isLight ? "bg-white/20 text-white backdrop-blur-sm" : "bg-emerald-500/20 text-emerald-400"}`}>
              <CircleDollarSign className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <h3 className="text-2xl font-black font-mono">৳{summary.total_collected.toLocaleString()}</h3>
            <p className={`text-[11px] mt-1 font-semibold ${isLight ? "text-emerald-100" : "text-emerald-400/80"}`}>
              Verified from delivered parcels
            </p>
          </div>
        </div>

        {/* Card 2: Delivered Orders */}
        <div
          className={`p-5 rounded-2xl sm:rounded-3xl shadow-lg relative overflow-hidden transition-all duration-300 ${
            isLight
              ? "bg-gradient-to-br from-blue-600 via-indigo-600 to-indigo-700 text-white shadow-blue-500/25 border-0 hover:scale-[1.01]"
              : "bg-gradient-to-br from-blue-950/40 to-slate-900 border border-blue-500/30 text-white"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className={`text-xs font-black uppercase tracking-wider ${isLight ? "text-blue-100" : "text-blue-400"}`}>
              Delivered Orders
            </span>
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${isLight ? "bg-white/20 text-white backdrop-blur-sm" : "bg-blue-500/20 text-blue-400"}`}>
              <PackageCheck className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <h3 className="text-2xl font-black font-mono">{summary.total_delivered_orders} Orders</h3>
            <p className={`text-[11px] mt-1 font-semibold ${isLight ? "text-blue-100" : "text-blue-400/80"}`}>
              Delivered to customers
            </p>
          </div>
        </div>

        {/* Card 3: Delivered Items Sold */}
        <div
          className={`p-5 rounded-2xl sm:rounded-3xl shadow-lg relative overflow-hidden transition-all duration-300 ${
            isLight
              ? "bg-gradient-to-br from-purple-600 via-violet-600 to-indigo-700 text-white shadow-purple-500/25 border-0 hover:scale-[1.01]"
              : "bg-gradient-to-br from-purple-950/40 to-slate-900 border border-purple-500/30 text-white"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className={`text-xs font-black uppercase tracking-wider ${isLight ? "text-purple-100" : "text-purple-400"}`}>
              Delivered Items Sold
            </span>
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${isLight ? "bg-white/20 text-white backdrop-blur-sm" : "bg-purple-500/20 text-purple-400"}`}>
              <ShoppingBag className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <h3 className="text-2xl font-black font-mono">{summary.total_items_sold} Items</h3>
            <p className={`text-[11px] mt-1 font-semibold ${isLight ? "text-purple-100" : "text-purple-400/80"}`}>
              Total products delivered
            </p>
          </div>
        </div>
      </div>

      {/* ৩. ফিল্টার ও কুইক ডেট রেঞ্জ সিলেক্টর */}
      <div
        className={`p-4 sm:p-5 rounded-2xl sm:rounded-3xl space-y-3 transition-all ${
          isLight
            ? "bg-white border border-slate-200/90 shadow-md shadow-slate-200/50"
            : "bg-slate-900/90 border border-slate-800 shadow-xl"
        }`}
      >
        <div className={`flex flex-wrap items-center gap-2 pb-2.5 border-b text-[11px] font-bold ${isLight ? "border-slate-100" : "border-slate-800"}`}>
          <span className={`flex items-center gap-1 mr-1 ${isLight ? "text-slate-500" : "text-slate-400"}`}>
            <Filter className="w-3.5 h-3.5 text-emerald-500" /> Quick Filter:
          </span>
          <button
            onClick={() => handleSetQuickDate("today")}
            className={`px-3 py-1 rounded-lg transition-colors cursor-pointer ${
              fromDate === getTodayDate() && toDate === getTodayDate()
                ? "bg-emerald-600 text-white font-bold"
                : isLight
                ? "bg-slate-100 text-slate-700 hover:bg-slate-200"
                : "bg-slate-950 text-slate-300 hover:text-white border border-slate-800"
            }`}
          >
            Today
          </button>
          <button
            onClick={() => handleSetQuickDate("yesterday")}
            className={`px-3 py-1 rounded-lg transition-colors cursor-pointer ${
              isLight
                ? "bg-slate-100 text-slate-700 hover:bg-slate-200"
                : "bg-slate-950 text-slate-300 hover:text-white border border-slate-800"
            }`}
          >
            Yesterday
          </button>
          <button
            onClick={() => handleSetQuickDate("month")}
            className={`px-3 py-1 rounded-lg transition-colors cursor-pointer ${
              isLight
                ? "bg-slate-100 text-slate-700 hover:bg-slate-200"
                : "bg-slate-950 text-slate-300 hover:text-white border border-slate-800"
            }`}
          >
            This Month
          </button>
          <button
            onClick={() => handleSetQuickDate("all")}
            className={`px-3 py-1 rounded-lg transition-colors cursor-pointer ${
              fromDate === "" && toDate === ""
                ? "bg-emerald-600 text-white font-bold"
                : isLight
                ? "bg-slate-100 text-slate-700 hover:bg-slate-200"
                : "bg-slate-950 text-slate-300 hover:text-white border border-slate-800"
            }`}
          >
            All Delivered Orders
          </button>
        </div>

        {/* Date Inputs */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <div className="relative">
            <label className={`font-bold block mb-1 ${isLight ? "text-slate-600" : "text-slate-400"}`}>FROM DATE</label>
            <div
              onClick={() => setActivePicker(activePicker === "from" ? null : "from")}
              className={`w-full px-3.5 py-2.5 rounded-xl border flex items-center justify-between cursor-pointer transition-colors ${
                isLight
                  ? "bg-slate-50 border-slate-200 text-slate-800 hover:border-emerald-500"
                  : "bg-slate-950 border-slate-800 text-white hover:border-emerald-500"
              }`}
            >
              <span className="font-mono text-xs font-semibold">
                {formatInputDisplay(fromDate)}
              </span>
              <CalendarIcon className="w-4 h-4 text-emerald-500" />
            </div>

            {activePicker === "from" && (
              <CustomDatePicker
                selectedDate={fromDate}
                onChange={(dateStr) => setFromDate(dateStr)}
                onClose={() => setActivePicker(null)}
                align="left"
              />
            )}
          </div>

          <div className="relative">
            <label className={`font-bold block mb-1 ${isLight ? "text-slate-600" : "text-slate-400"}`}>TO DATE</label>
            <div
              onClick={() => setActivePicker(activePicker === "to" ? null : "to")}
              className={`w-full px-3.5 py-2.5 rounded-xl border flex items-center justify-between cursor-pointer transition-colors ${
                isLight
                  ? "bg-slate-50 border-slate-200 text-slate-800 hover:border-emerald-500"
                  : "bg-slate-950 border-slate-800 text-white hover:border-emerald-500"
              }`}
            >
              <span className="font-mono text-xs font-semibold">
                {formatInputDisplay(toDate)}
              </span>
              <CalendarIcon className="w-4 h-4 text-emerald-500" />
            </div>

            {activePicker === "to" && (
              <CustomDatePicker
                selectedDate={toDate}
                onChange={(dateStr) => setToDate(dateStr)}
                onClose={() => setActivePicker(null)}
                align="left"
              />
            )}
          </div>

          <div>
            <label className={`font-bold block mb-1 ${isLight ? "text-slate-600" : "text-slate-400"}`}>PAYMENT METHOD</label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className={`w-full px-3.5 py-2.5 rounded-xl border focus:outline-emerald-500 cursor-pointer font-medium ${
                isLight
                  ? "bg-slate-50 border-slate-200 text-slate-800"
                  : "bg-slate-950 border-slate-800 text-white"
              }`}
            >
              <option value="all">All Methods</option>
              <option value="cod">Cash on Delivery (COD)</option>
              <option value="bkash">bKash</option>
              <option value="nagad">Nagad</option>
              <option value="card">Card / Online</option>
              <option value="sslcommerz">SSLCommerz</option>
            </select>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative pt-1 text-xs">
          <Search className={`w-4 h-4 absolute left-3.5 top-3.5 ${isLight ? "text-slate-400" : "text-slate-400"}`} />
          <input
            type="text"
            placeholder="Search by Order ID (#ORD-101), Customer Name, Mobile Number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`w-full pl-10 pr-4 py-2.5 rounded-xl border font-medium focus:outline-emerald-500 ${
              isLight
                ? "bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 focus:bg-white"
                : "bg-slate-950 border-slate-800 text-white placeholder:text-slate-500"
            }`}
          />
        </div>
      </div>

      {/* ৪. ডেটা টেবিল */}
      <div
        className={`rounded-2xl sm:rounded-3xl overflow-hidden transition-all ${
          isLight
            ? "bg-white border border-slate-200/90 shadow-md shadow-slate-200/50"
            : "bg-slate-900/90 border border-slate-800 shadow-2xl"
        }`}
      >
        <div className="overflow-x-auto w-full">
          <table className="w-full text-left text-xs min-w-[850px]">
            <thead
              className={`uppercase text-[10px] font-black border-b tracking-wider ${
                isLight
                  ? "bg-slate-50 text-slate-600 border-slate-200"
                  : "bg-slate-950/70 text-slate-400 border-slate-800"
              }`}
            >
              <tr>
                <th className="py-3.5 px-4">Date & Time</th>
                <th className="py-3.5 px-4">Order #</th>
                <th className="py-3.5 px-4">Customer Name</th>
                <th className="py-3.5 px-4">Mobile No.</th>
                <th className="py-3.5 px-4">Products / Items</th>
                <th className="py-3.5 px-4 text-center">Payment Method</th>
                <th className="py-3.5 px-4 text-right">Collected Amount</th>
                <th className="py-3.5 px-4 text-center">Status</th>
              </tr>
            </thead>

            <tbody className={`divide-y font-medium ${isLight ? "divide-slate-100 text-slate-700" : "divide-slate-800/80 text-slate-300"}`}>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-16 text-slate-400">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-emerald-500 mb-2" />
                    <span>Loading {branchInfo?.name} delivered collection records...</span>
                  </td>
                </tr>
              ) : paginatedOrders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-16 text-slate-500 space-y-2">
                    <AlertCircle className="w-8 h-8 mx-auto text-slate-400 mb-1" />
                    <p>No delivered orders found for selected date: <strong className={`font-mono ${isLight ? "text-slate-800" : "text-white"}`}>{formatInputDisplay(fromDate)}</strong></p>
                    <button
                      onClick={() => handleSetQuickDate("all")}
                      className="text-xs text-emerald-500 hover:underline font-bold cursor-pointer"
                    >
                      Click here to load All Delivered Orders ({summary.total_delivered_orders})
                    </button>
                  </td>
                </tr>
              ) : (
                paginatedOrders.map((order: any) => (
                  <tr key={order.id} className={isLight ? "hover:bg-slate-50 transition-colors" : "hover:bg-slate-800/30 transition-colors"}>
                    <td className="py-3.5 px-4 text-slate-400 font-mono text-[11px] whitespace-nowrap">
                      {order.date}
                    </td>

                    <td className="py-3.5 px-4 font-mono font-bold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                      #{order.order_number}
                    </td>

                    <td className={`py-3.5 px-4 font-bold whitespace-nowrap ${isLight ? "text-slate-900" : "text-white"}`}>
                      {order.customer_name}
                    </td>

                    <td className="py-3.5 px-4 font-mono text-slate-400 whitespace-nowrap">
                      {order.customer_phone}
                    </td>

                    <td className="py-3.5 px-4">
                      <div className="max-w-[260px] truncate" title={order.products}>
                        <span className={`font-semibold ${isLight ? "text-slate-800" : "text-slate-200"}`}>{order.products}</span>
                      </div>
                    </td>

                    <td className="py-3.5 px-4 text-center whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                        isLight
                          ? "bg-slate-100 text-slate-700 border border-slate-200"
                          : "bg-slate-800 text-slate-300 border border-slate-700"
                      }`}>
                        {order.payment_method}
                      </span>
                    </td>

                    <td className="py-3.5 px-4 text-right font-bold text-emerald-600 dark:text-emerald-400 font-mono text-sm whitespace-nowrap">
                      ৳{order.amount.toLocaleString()}
                    </td>

                    <td className="py-3.5 px-4 text-center whitespace-nowrap">
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                        Delivered
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>

            {orders.length > 0 && (
              <tfoot className={`font-black border-t-2 text-xs ${isLight ? "bg-slate-50 border-slate-200" : "bg-slate-950 border-slate-800"}`}>
                <tr>
                  <td colSpan={6} className={`py-3.5 px-4 uppercase tracking-wider ${isLight ? "text-slate-700" : "text-slate-300"}`}>
                    Total Delivered Collection ({orders.length} orders)
                  </td>
                  <td className="py-3.5 px-4 text-right text-emerald-600 dark:text-emerald-400 font-mono text-sm">
                    ৳{summary.total_collected.toLocaleString()}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Pagination */}
        {orders.length > 0 && (
          <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t text-xs ${
            isLight ? "bg-slate-50/50 border-slate-200" : "bg-slate-950/40 border-slate-800"
          }`}>
            <div className="flex items-center gap-3 text-slate-400">
              <span>
                Showing <strong className={isLight ? "text-slate-900" : "text-white"}>{startIndex + 1}</strong> to{" "}
                <strong className={isLight ? "text-slate-900" : "text-white"}>{endIndex}</strong> of{" "}
                <strong className={isLight ? "text-slate-900" : "text-white"}>{orders.length}</strong> delivered orders
              </span>

              <div className={`flex items-center gap-1.5 pl-2 border-l ${isLight ? "border-slate-200" : "border-slate-800"}`}>
                <span className="text-[11px]">Show:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className={`rounded-lg px-2 py-1 text-[11px] focus:outline-emerald-500 cursor-pointer ${
                    isLight ? "bg-white border border-slate-200 text-slate-800" : "bg-slate-900 border border-slate-800 text-white"
                  }`}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-[11px]">entries</span>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                className={`p-1.5 rounded-lg border disabled:opacity-40 cursor-pointer ${
                  isLight
                    ? "bg-white border-slate-200 text-slate-700 hover:bg-slate-100"
                    : "bg-slate-900 border-slate-800 text-slate-300 hover:text-white"
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((page) => totalPages <= 5 || page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                  .map((page) => (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setCurrentPage(page)}
                      className={`min-w-[28px] h-7 px-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                        currentPage === page
                          ? "bg-emerald-600 text-white shadow-md shadow-emerald-600/30"
                          : isLight
                          ? "bg-white text-slate-700 hover:bg-slate-100 border border-slate-200"
                          : "bg-slate-900 text-slate-400 hover:text-white border border-slate-800"
                      }`}
                    >
                      {page}
                    </button>
                  ))}
              </div>

              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                className={`p-1.5 rounded-lg border disabled:opacity-40 cursor-pointer ${
                  isLight
                    ? "bg-white border-slate-200 text-slate-700 hover:bg-slate-100"
                    : "bg-slate-900 border-slate-800 text-slate-300 hover:text-white"
                }`}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}